/** 
 * @namespace Auth
 * @memberof Promega.Shell
 * @description A set of Ajax functions that can be used to log in, log out, create new user accounts, and more.
 */

import { AUTH } from '../react/config/API.js';
import getTestEnvironment from '../util/getTestEnvironment.js';

/**
 * @function login
 * @memberof Promega.Shell.Auth
 * @description Logs the user in.
 * @param {string} username
 * @param {string} password
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function login(username, password) {
    return new Promise((resolveLogin, rejectLogin) => {
        let resultObj;

        fetch(`https://${getTestEnvironment() || "www"}.promega.com` + AUTH.LOGIN, {
            credentials: "include",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify({ username, password })
        }).then(result => {
            resultObj = result;
            return result.json();
        }).then(jsonData => {
            if (resultObj.ok) {
                // This updates your xDB data after login (since WebAPI calls can't update xDB)
                fetch(AUTH.LOGINXDB, {
                    credentials: "same-origin",
                    method: "POST"
                }).then(resolveLogin(jsonData));
            }
            else rejectLogin(jsonData);
        }).catch(() => rejectLogin());
    });
}

/**
 * @function logout
 * @memberof Promega.Shell.Auth
 * @description Logs the user out.
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function logout() {
    return new Promise((resolveLogout, rejectLogout) => {
        fetch(`https://${getTestEnvironment() || "www"}.promega.com` + AUTH.LOGOUT, {
            credentials: "include",
            method: "POST"
        }).then(result => {
            if (result.ok) {
                // This resets your xDB data after logout, so someone else can use your PC and get new data
                fetch(AUTH.LOGOUTXDB, {
                    credentials: "same-origin",
                    method: "POST"
                }).then(result2 => {
                    // cookie.remove('SC_ANALYTICS_GLOBAL_COOKIE'); Only need to worry about this on shared PCs... don't implement yet
                    resolveLogout();
                });
            }
            else rejectLogout();
        }).catch(() => rejectLogout());
    });
}

/**
 * @function register
 * @memberof Promega.Shell.Auth
 * @description Registers for a new user account.
 * @param {Object} userData 
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function register(userData) {
    return new Promise((resolveRegister, rejectRegister) => {
        let resultObj;
        fetch(AUTH.REGISTER, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(userData)
        }).then(result => {
            resultObj = result;
            return result.json();
        }).then(jsonData => {
            if (resultObj.ok) resolveRegister(jsonData);
            else rejectRegister(jsonData);
        }).catch(() => rejectRegister());
    });
}

/**
 * @function forgotPassword
 * @memberof Promega.Shell.Auth
 * @description Requests a "forgot password" email.
 * @param {string} username
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function forgotPassword(username) {
    return new Promise((resolve, reject) => {
        let resultObj;
        fetch(`${AUTH.FORGOT}?username=${encodeURIComponent(username)}`, {
            credentials: "same-origin"
        }).then(result => {
            resultObj = result;
            return result.json();
        }).then(jsonData => {
            if (resultObj.ok) resolve(jsonData);
            else reject(jsonData);
        }).catch(() => reject());
    });
}

/**
 * @function resetPassword
 * @memberof Promega.Shell.Auth
 * @description Requests setting a new password for the user.
 * @param {object} config
 * @param {string} config.password
 * @param {string} config.loginRpId
 * @param {string} config.oldPassword
 * @param {string} config.username
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function resetPassword(config) {
    const password = config.password;
    const queryValue = config.loginRpId;
    const oldPassword = config.oldPassword;
    const username = config.username;

    return new Promise((resolve, reject) => {
        const bodyData = {
            newPassword: password
        };
        if (typeof queryValue === "string")
            bodyData.loginRpId = queryValue;
        if (typeof oldPassword === 'string')
            bodyData.oldPassword = oldPassword;
        if (typeof username === 'string')
            bodyData.username = username;

        fetch(AUTH.RESET_PASSWORD, {
            credentials: "same-origin",
            method: "POST",
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(bodyData)
        }).then(result => {
            if (result.ok) resolve();
            else result.json().then(jsonData => reject(jsonData));
        }).catch(() => reject());
    });
}

/**
 * @function resendVerification
 * @memberof Promega.Shell.Auth
 * @description Requests an account verification email to be sent.
 * @param {string} username
 * @returns {Promise} A promise that is resolved when the operation succeeds and is rejected when it fails.
 */
export function resendVerification(username) {
    return new Promise((resolve, reject) => {
        fetch(`${AUTH.RESEND_VERIFICATION}?email=${encodeURIComponent(username)}`, {
            credentials: "same-origin"
        }).then(result => {
            if (result.ok) resolve();
            else result.json().then(jsonData => reject(jsonData));
        }).catch(() => reject());
    });
}
